import { fetchRedeemableItem } from '@/api/account';
import { IFreeData } from '@/api/types/account';
import { IProductDetail } from '@/api/types/public';
import { useJump } from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import React, { useContext, useState } from 'react';

interface FixedBottomButtonProps {
  detail: IProductDetail & {
    products_category?: string;
    is_glass?: number;
    product_lens?: number;
    shop_sku?: string;
    business_sku?: string;
    sku?: string;
    short_name?: string;
    qty?: number;
    status?: number;
  };
  freeData: IFreeData & {
    discount_coupons?: Array<{
      id?: number;
    }>;
    free_available_count?: number;
  };
  refreshFreeData?: () => void;
  containerStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
}

const FixedBottomButton: React.FC<FixedBottomButtonProps> = ({
  detail,
  freeData,
  refreshFreeData,
  containerStyle,
  buttonStyle,
}) => {
  const t = useTranslations('account.redeemableItems.RedeemedDetailDialog.fixedBottomButton');
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const currency = useStore((state) => state.currency);
  const token = useStore((state) => state.token);
  const clientUserInfo = useStore((state) => state.userInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const [joinMemberLoading, setJoinMemberLoading] = useState(false);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const setJoinFreeDialogInfo = useStore((state) => state.setJoinFreeDialogInfo);
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const { deviceType } = useContext(ResponsiveContext);
  const toJumpTarget = useJump();
  const FixedButtonContainer = styled(Box)(() => ({
    position: deviceType === EPlatform.WEB ? 'absolute' : 'fixed',
    bottom: deviceType === EPlatform.WEB ? '-16px' : '0',
    right: deviceType === EPlatform.WEB ? 'auto' : '0',
    width: deviceType === EPlatform.WEB ? 'calc(100% - 478px)' : '100%',
    left: deviceType === EPlatform.WEB ? '432px' : 0,
    backgroundColor: '#ffffff',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    flexDirection: deviceType === EPlatform.WEB ? 'row' : 'row-reverse',
    paddingBottom: deviceType === EPlatform.WEB ? '16px' : '0',
    paddingTop: deviceType === EPlatform.WEB ? '16px' : '0',
    ...containerStyle,
  }));
  const SelectButton = styled(Button)(() => ({
    borderRadius: '4px',
    height: '48px',
    fontSize: '14px',
    textTransform: 'none',
    width: '100%',
    fontFamily: font.bb,
    display: 'block',
    boxShadow: 'none',
    ...buttonStyle,
  }));

  const signIn = () => {
    if (token) {
      handleJoinMember();
    } else {
      setShowLoginRegisterDialog(true);
    }
  };

  const handleJoinMember = async () => {
    // try {
    //   setJoinMemberLoading(true);
    //   await postJoinMember({});
    //   await refreshAccountInfo();
    // } catch (error) {
    //   console.error('Failed to join member:', error);
    // } finally {
    //   setJoinMemberLoading(false);
    // }
    setJoinMemberLoading(true);
    setJoinFreeDialogInfo({
      show: true,
    });
    setJoinMemberLoading(false);
    setTimeout(() => {
      refreshAccountInfo();
    }, 3000);
  };

  const SDAddToCartBtnClick = () => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    const sdEventName = 'AddToCartBtn_Click';
    const sdEventParams = {
      product_view_uuid: '',
      entrance_edit_lens_scene: '',
      edit_lens_type: '',
      add_cart_scene: '兑换券加购',
      product_class_name: detail?.products_category,
      is_product_lens: detail?.is_glass === 1 && detail?.product_lens === 1,
      shop_sku: detail?.shop_sku,
      business_sku: detail?.business_sku,
      product_sku: detail?.sku,
      product_id: `${detail?.id || ''}`,
      product_name: detail?.short_name,
      refer_page_name,
      refer_url_path,
      is_glass: detail?.is_glass === 1,
      prescription_type_name: '',
      coating_id: '',
      coating_type_name: '',
      coating_price: '',
      current_coating_price: '',
      lens_id: '',
      lens_type_name: '',
      lens_color: '',
      lens_price: '',
      current_lens_price: '',
      lens_index_name: '',
      lens_index_price: '',
      current_lens_index_price: '',
      product_price: '',
      current_product_price: '',
      currency: currency.name,
    };
    trackEvent(
      {
        eventName: sdEventName,
        properties: sdEventParams,
      },
      [ProviderType.SENSORS],
    );
  };

  const SDAddToCartResult = (code: string, success: boolean, msg: string) => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    const sdEventName = 'AddToCart_Result';
    const sdEventParams = {
      product_view_uuid: '',
      refer_page_name,
      refer_url_path,
      prescription_type_name: '',
      coating_id: '',
      coating_type_name: '',
      coating_price: '',
      current_coating_price: '',
      lens_id: '',
      lens_type_name: '',
      lens_color: '',
      lens_price: '',
      current_lens_price: '',
      lens_index_name: '',
      lens_index_price: '',
      current_lens_index_price: '',
      product_price: '',
      current_product_price: '',
      currency: currency.name,
      dollar_exchange_rate: parseFloat(currency.exchange_rate),
      shop_sku: detail?.shop_sku,
      business_sku: detail?.business_sku,
      product_sku: detail?.sku,
      product_id: `${detail?.id || ''}`,
      product_name: detail?.short_name,
      is_glass: detail?.is_glass === 1,
      is_success: success,
      entrance_edit_lens_scene: '',
      edit_lens_type: '',
      add_cart_scene: '兑换券加购',
      reorder_order_no: '',
      product_class_name: detail?.products_category,
      is_product_lens: detail?.is_glass === 1 && detail?.product_lens === 1,
      service_code: code,
      msg,
    };
    trackEvent(
      {
        eventName: sdEventName,
        properties: sdEventParams,
      },
      [ProviderType.SENSORS],
    );
  };

  const handleRedeem = async () => {
    try {
      SDAddToCartBtnClick();
      setBtnLoading(true);
      const res = (await fetchRedeemableItem({
        product_id: detail?.id || 0,
        coupon_id: freeData?.discount_coupons?.[0]?.id || 0,
      })) as { code: number; data: any; msg: string };
      const { code, data, msg } = res;
      if (code === 200) {
        SDAddToCartResult(`${code}`, true, msg);
        toast.success(t('redemptionSuccessful'));
        sessionStorage.setItem('zl-sensors-entrance-cart-scene', '兑换券加购');
        localStorage.setItem('zl-justAddToCart', JSON.stringify(data?.cart_ids || []));
        toJumpTarget({
          jump_type: 3,
          Jump_target: '/cart',
        });
      } else {
        SDAddToCartResult(`${code}`, false, msg);
        toast.error(msg);
      }
      if (refreshFreeData) {
        refreshFreeData();
      }
    } catch (error) {
      logger.log(error);
      SDAddToCartResult(`${error?.code || ''}`, false, error?.msg || '');
    } finally {
      setBtnLoading(false);
    }
  };

  const renderMemberSelectButton = () => {
    return (freeData?.free_available_count || 0) > 0 ? (
      <SelectButton
        color="primary"
        onClick={handleRedeem}
        loading={btnLoading}
        variant="contained"
        disabled={btnLoading}
      >
        {t('redeemedItem')}
      </SelectButton>
    ) : (
      <SelectButton variant="contained" disabled>
        <Box sx={{ color: '#ffffff' }}>{t('noAvailableCoupons')}</Box>
      </SelectButton>
    );
  };

  return (
    <>
      <FixedButtonContainer>
        {(() => {
          // 非会员或未登录
          if (clientUserInfo.is_member !== 1 || !token) {
            return (
              <SelectButton
                color="primary"
                loading={joinMemberLoading}
                onClick={signIn}
                variant="contained"
              >
                {t('joinForFree')}
              </SelectButton>
            );
          }
          // 商品售罄
          else if ((detail?.qty || 0) <= 0) {
            return (
              <SelectButton color="primary" disabled variant="contained">
                {t('soldOut')}
              </SelectButton>
            );
          }
          // 商品补货中
          else if (detail?.status === 2) {
            return (
              <SelectButton color="primary" disabled variant="contained">
                {t('restock')}
              </SelectButton>
            );
          }
          // 会员可用按钮
          else if (clientUserInfo.is_member === 1) {
            return renderMemberSelectButton();
          }
          // 兜底情况
          return null;
        })()}
      </FixedButtonContainer>
    </>
  );
};

export default FixedBottomButton;
